const defaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
};

export const createDefaultEventForm = ({
  channelId = '',
  contextType = 'GROUP',
  publishMode,
  schedulePreset = null,
  userId = '',
  otherUserId = '',
} = {}) => {
  const startDate = schedulePreset?.startDate || '';
  const startTime = schedulePreset?.startTime || '';
  const endDate = schedulePreset?.endDate || startDate;
  const endTime = schedulePreset?.endTime || '';

  const participants = [];
  if (userId) {
    participants.push({ userId, role: 'REQUIRED', status: 'ACCEPTED' });
  }
  if (otherUserId && otherUserId !== userId) {
    participants.push({ userId: otherUserId, role: 'REQUIRED', status: 'INVITED' });
  }

  return {
    name: '',
    description: '',
    eventType: 'MEETING',
    category: '',
    tags: [],
    channelId,
    contextType,
    publishMode: publishMode
      || (!channelId || contextType === 'PERSONAL' ? 'ANNOUNCEMENT' : 'CHANNEL'),
    schedule: {
      startDate,
      startTime,
      endDate,
      endTime,
      timezone: defaultTimezone(),
      allDay: false,
      recurrence: { type: 'none' },
      responseDeadline: '',
      responseDeadlineTime: '',
      expiresAt: schedulePreset?.expiresAt || endDate || '',
      expiresAtTime: schedulePreset?.expiresAtTime || endTime || '',
    },
    participants: {
      mode: contextType === 'DM' ? 'DM_AUTO' : 'EVERYONE',
      required: participants,
      optional: [],
      guests: [],
      maxParticipants: '',
      minParticipants: '',
      allowInviteOthers: false,
    },
    responsePolicy: {
      type: 'ACCEPT_REJECT',
      pollQuestion: '',
      pollOptions: ['Option 1', 'Option 2'],
      allowMultiple: false,
      anonymous: false,
      allowVoteChange: true,
      pollDeadline: '',
      rsvpFields: {
        guestCount: { enabled: false, required: false },
        guestNames: { enabled: false, required: false },
        guestAges: { enabled: false, required: false },
        foodPreference: { enabled: false, required: false },
        drinkPreference: { enabled: false, required: false },
        customFields: [],
      },
    },
    configuration: {},
    rules: [],
    reminderPolicy: {
      enabled: true,
      offsets: ['1d', '1h'],
      conditional: [{ trigger: 'PENDING_RESPONSE', offset: '24h' }],
    },
    notificationPolicy: {
      channels: ['IN_APP', 'PUSH'],
      recipients: ['ALL_PARTICIPANTS'],
    },
    completionActions: [],
    permissions: {
      modifyPolicy: 'CREATOR_ONLY',
      canAddParticipants: true,
      canInviteOthers: false,
      canAddExpenses: true,
      canModifyResponses: true,
      canCancel: false,
      canTriggerFollowUp: false,
    },
    visibility: {
      scope: 'PARTICIPANTS_ONLY',
      showResponsesToParticipants: true,
      showExpensesToParticipants: true,
      showCalculationsToParticipants: true,
    },
    location: {
      locationType: 'PHYSICAL',
      venue: '',
      address: '',
      mapUrl: '',
      parkingInfo: '',
      meetingUrl: '',
      meetingId: '',
      meetingPassword: '',
    },
    attachments: [],
    templateId: '',
  };
};

export const applyTemplateToForm = (form, template) => {
  if (!template?.preset) return form;
  const preset = template.preset;
  return {
    ...form,
    eventType: preset.eventType || form.eventType,
    category: preset.category || form.category,
    responsePolicy: { ...form.responsePolicy, ...(preset.responsePolicy || {}) },
    configuration: { ...form.configuration, ...(preset.configuration || {}) },
    reminderPolicy: { ...form.reminderPolicy, ...(preset.reminderPolicy || {}) },
    completionActions: preset.completionActions || form.completionActions,
    location: { ...form.location, ...(preset.location || {}) },
    templateId: template.id,
  };
};

export const eventDtoToForm = (event, defaults = {}) => {
  const base = createDefaultEventForm(defaults);
  if (!event) return base;

  const schedule = event.schedule || {};
  const {
    participantMode,
    maxParticipants,
    minParticipants,
    allowInviteOthers,
    ...scheduleFields
  } = schedule;

  const required = [];
  const optional = [];
  const guests = [];
  (event.participants || []).forEach((p) => {
    if (!p?.userId) return;
    if (p.role === 'OPTIONAL') optional.push(p);
    else if (p.role === 'GUEST') guests.push(p);
    else required.push(p);
  });

  return {
    ...base,
    id: event.id,
    status: event.status,
    name: event.name || '',
    description: event.description || '',
    eventType: event.eventType || base.eventType,
    category: event.category || '',
    tags: Array.isArray(event.tags) ? event.tags : [],
    channelId: event.channelId || defaults.channelId || '',
    contextType: event.contextType || defaults.contextType || base.contextType,
    publishMode: event.publishMode || defaults.publishMode || base.publishMode,
    schedule: {
      ...base.schedule,
      ...scheduleFields,
    },
    participants: {
      ...base.participants,
      mode: participantMode || base.participants.mode,
      required: required.length ? required : base.participants.required,
      optional,
      guests,
      maxParticipants: maxParticipants ?? '',
      minParticipants: minParticipants ?? '',
      allowInviteOthers: Boolean(allowInviteOthers),
    },
    responsePolicy: {
      ...base.responsePolicy,
      ...(event.responsePolicy || {}),
      rsvpFields: {
        ...base.responsePolicy.rsvpFields,
        ...(event.responsePolicy?.rsvpFields || {}),
      },
    },
    configuration: event.configuration || {},
    rules: Array.isArray(event.rules) ? event.rules : [],
    reminderPolicy: { ...base.reminderPolicy, ...(event.reminderPolicy || {}) },
    notificationPolicy: { ...base.notificationPolicy, ...(event.notificationPolicy || {}) },
    completionActions: Array.isArray(event.completionActions) ? event.completionActions : [],
    permissions: { ...base.permissions, ...(event.permissions || {}) },
    visibility: { ...base.visibility, ...(event.visibility || {}) },
    location: { ...base.location, ...(event.location || {}) },
    attachments: Array.isArray(event.attachments) ? event.attachments : [],
    templateId: event.templateId || '',
  };
};

const weekdayFromDate = (dateStr) => {
  const parsed = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
  return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][parsed.getDay()];
};

const normalizeEventRecurrence = (recurrence, startDate) => {
  const type = String(recurrence?.type || 'none').toLowerCase();
  const normalized = ['daily', 'weekly', 'monthly'].includes(type) ? type : 'none';
  const out = { type: normalized };
  if (normalized === 'weekly') {
    const days = Array.isArray(recurrence?.daysOfWeek) ? recurrence.daysOfWeek.filter(Boolean) : [];
    out.daysOfWeek = days.length ? days : [weekdayFromDate(startDate)];
  }
  if (normalized === 'monthly') {
    const days = Array.isArray(recurrence?.daysOfMonth)
      ? recurrence.daysOfMonth.map((d) => Number(d)).filter((d) => d >= 1 && d <= 31)
      : [];
    const fallback = startDate ? Number(String(startDate).slice(8, 10)) : new Date().getDate();
    out.daysOfMonth = days.length ? days : [fallback];
  }
  return out;
};

export const formToApiPayload = (form, { draft = false, idempotencyKey } = {}) => {
  const allParticipants = [
    ...(form.participants?.required || []).map((p) => ({ ...p, role: p.role || 'REQUIRED' })),
    ...(form.participants?.optional || []).map((p) => ({ ...p, role: 'OPTIONAL' })),
    ...(form.participants?.guests || []).map((p) => ({ ...p, role: 'GUEST' })),
  ];
  const isPoll = form.eventType === 'POLL';
  const isAction = form.eventType === 'ACTION_TRACKING';
  const pollQuestion = (form.responsePolicy?.pollQuestion || form.name || '').trim();
  const pollOptions = (form.responsePolicy?.pollOptions || []).map((o) => String(o || '').trim()).filter(Boolean);
  const rawSchedule = form.schedule || {};
  const schedule = {
    ...rawSchedule,
    recurrence: normalizeEventRecurrence(rawSchedule.recurrence, rawSchedule.startDate),
    participantMode: form.participants?.mode,
    maxParticipants: form.participants?.maxParticipants || null,
    minParticipants: form.participants?.minParticipants || null,
    allowInviteOthers: form.participants?.allowInviteOthers,
  };
  if (!String(schedule.startDate || '').trim()) {
    schedule.startDate = '';
    schedule.startTime = '';
  }
  if (!String(schedule.endDate || '').trim()) {
    schedule.endDate = '';
    schedule.endTime = '';
  }
  if (!String(schedule.expiresAt || '').trim()) {
    schedule.expiresAt = '';
    schedule.expiresAtTime = '';
  }

  if (isAction) {
    schedule.startDate = '';
    schedule.startTime = '';
    schedule.endDate = '';
    schedule.endTime = '';
    schedule.expiresAt = '';
    schedule.expiresAtTime = '';
    schedule.allDay = true;
    schedule.endUndecided = true;
    schedule.participantMode = form.configuration?.actionShare ? 'SELECT' : 'SELF';
    schedule.allowInviteOthers = true;
  }

  return {
    name: isPoll ? pollQuestion : form.name.trim(),
    description: form.description || '',
    eventType: form.eventType,
    category: isAction
      ? (form.configuration?.actionShare ? 'SOCIAL' : 'PERSONAL')
      : form.category || undefined,
    tags: form.tags || [],
    channelId: form.channelId || undefined,
    contextType: form.contextType,
    publishMode: form.publishMode
      || (!form.channelId || form.contextType === 'PERSONAL' ? 'ANNOUNCEMENT' : 'CHANNEL'),
    schedule,
    participants: allParticipants,
    responsePolicy: isAction
      ? { ...form.responsePolicy, type: 'NONE' }
      : isPoll
      ? {
        ...form.responsePolicy,
        type: 'POLL',
        pollQuestion,
        pollOptions,
        allowMultiple: Boolean(form.responsePolicy?.allowMultiple),
      }
      : form.responsePolicy,
    configuration: isAction
      ? {
        trackKind: String(form.configuration?.trackKind || 'NUMBER').toUpperCase() === 'COUNT'
          ? 'COUNT'
          : 'NUMBER',
        entities: Array.isArray(form.configuration?.entities) ? form.configuration.entities : [],
        entries: Array.isArray(form.configuration?.entries) ? form.configuration.entries : [],
        actionShare: Boolean(form.configuration?.actionShare),
      }
      : isPoll
      ? {
        ...(form.configuration || {}),
        question: pollQuestion,
        options: pollOptions,
        allowMultiple: Boolean(form.responsePolicy?.allowMultiple),
      }
      : form.configuration,
    rules: form.rules,
    reminderPolicy: isAction || !String(schedule.startDate || '').trim()
      ? { ...(form.reminderPolicy || {}), enabled: false, offsets: [] }
      : form.reminderPolicy,
    notificationPolicy: form.notificationPolicy,
    completionActions: form.completionActions,
    permissions: isAction
      ? { ...(form.permissions || {}), canInviteOthers: true, canAddParticipants: true }
      : form.permissions,
    visibility: form.visibility,
    location: form.location,
    attachments: form.attachments,
    templateId: form.templateId || undefined,
    idempotencyKey,
    draft,
  };
};

export const formToUpdatePayload = (form, { draft = false, editingStatus } = {}) => {
  const { idempotencyKey, ...payload } = formToApiPayload(form, { draft });
  if (editingStatus && editingStatus !== 'DRAFT' && !draft) {
    delete payload.draft;
  }
  return payload;
};
